<script setup lang="ts">
  import { computed } from 'vue';
  import { usePropertyForm } from '../../../model/usePropertyForm';
  import type {
    NumberInputProperties as NumberInputPropsType,
    NumberType,
  } from '../../../model/form.types';
  import { FORM_FIELD_CHARACTER_LIMITS } from '../../../lib/form-field-character-limits';
  import {
    numberInputPropertiesSchema,
    numberTypingFrom,
    toCanonicalNumber,
    toDecimalPlaces,
    toNumberDisplayText,
    valueMaskFrom,
    type NumberInputFormValues,
  } from '../../../lib/numberInputPropertiesValidation';
  import DecimalPlacesField from './DecimalPlacesField.vue';
  import NumberTextField from './NumberTextField.vue';
  import {
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
    IconRadio,
    Input,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Switch,
  } from '@/shared/ui';

  interface Props {
    elementId?: string;
  }

  const props = defineProps<Props>();

  const NUMBER_TYPE_OPTIONS: { value: NumberType; label: string }[] = [
    { value: 'integer', label: 'Número inteiro' },
    { value: 'decimal', label: 'Decimal' },
  ];

  const { values } = usePropertyForm<NumberInputFormValues, NumberInputPropsType>({
    elementId: () => props.elementId,
    validationSchema: numberInputPropertiesSchema,
    extractInitialValues: (properties: NumberInputPropsType | undefined): NumberInputFormValues => ({
      name: properties?.name ?? '',
      value: toNumberDisplayText(properties?.value),
      placeholder: properties?.placeholder ?? '',
      label: properties?.label ?? '',
      labelPosition: properties?.labelPosition ?? 'top',
      required: properties?.required ?? false,
      numberType: properties?.numberType,
      decimalPlaces: properties?.decimalPlaces === undefined ? '' : String(properties.decimalPlaces),
      hasMinValue: properties?.minValue !== undefined,
      hasMaxValue: properties?.maxValue !== undefined,
      minValue: toNumberDisplayText(properties?.minValue),
      maxValue: toNumberDisplayText(properties?.maxValue),
    }),
    mapValuesToProperties: (values: NumberInputFormValues, currentProperties: NumberInputPropsType) => ({
      properties: {
        ...currentProperties,
        name: values.name || currentProperties.name,
        value: toCanonicalNumber(values.value),
        placeholder: values.placeholder || undefined,
        label: values.label || undefined,
        labelPosition: values.labelPosition,
        required: values.required ?? false,
        numberType: values.numberType,
        decimalPlaces: values.numberType === 'decimal' ? toDecimalPlaces(values.decimalPlaces) : undefined,
        minValue: values.hasMinValue ? toCanonicalNumber(values.minValue) : undefined,
        maxValue: values.hasMaxValue ? toCanonicalNumber(values.maxValue) : undefined,
      },
    }),
  });

  const typing = computed(() => numberTypingFrom(values));
  const valueMask = computed(() => valueMaskFrom(values));
</script>

<template>
  <div class="flex flex-col">
    <!-- ID do campo -->
    <div class="border-b border-gray-02 py-4">
      <FormField
        v-slot="{ componentField }"
        name="name"
      >
        <FormItem class="flex flex-col gap-2">
          <FormLabel class="text-sm text-gray-11 font-normal">
            ID do campo
          </FormLabel>
          <FormControl>
            <Input
              v-bind="componentField"
              placeholder="nome_do_campo"
              class="h-11"
              :maxlength="FORM_FIELD_CHARACTER_LIMITS.FIELD_ID"
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>
    </div>

    <!-- Valor -->
    <div class="border-b border-gray-02 py-4">
      <NumberTextField
        name="value"
        label="Valor"
        placeholder="Digite aqui o valor"
        :typing="typing"
        :mask="valueMask"
        :maxlength="FORM_FIELD_CHARACTER_LIMITS.VALUE"
      />
    </div>

    <!-- Texto auxiliar -->
    <div class="border-b border-gray-02 py-4">
      <FormField
        v-slot="{ componentField }"
        name="placeholder"
      >
        <FormItem class="flex flex-col gap-2">
          <FormLabel class="text-sm text-gray-11 font-normal">
            Texto auxiliar
          </FormLabel>
          <FormControl>
            <Input
              v-bind="componentField"
              placeholder="Digite aqui o texto auxiliar"
              class="h-11"
              :maxlength="FORM_FIELD_CHARACTER_LIMITS.AUXILIARY_TEXT"
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>
    </div>

    <!-- Rótulo + Posicionamento -->
    <div class="border-b border-gray-02 py-4 flex flex-col gap-[10px]">
      <FormField
        v-slot="{ componentField }"
        name="label"
      >
        <FormItem class="flex flex-col gap-2">
          <FormLabel class="text-sm text-gray-11 font-normal">
            Rótulo
          </FormLabel>
          <FormControl>
            <Input
              v-bind="componentField"
              placeholder="Digite aqui"
              class="h-[42px]"
              :maxlength="FORM_FIELD_CHARACTER_LIMITS.LABEL"
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>

      <FormField
        v-slot="{ componentField }"
        name="labelPosition"
      >
        <FormItem class="flex flex-col gap-2">
          <FormLabel class="text-sm text-gray-11 font-normal">
            Posicionamento
          </FormLabel>
          <Select v-bind="componentField">
            <FormControl>
              <SelectTrigger class="h-11 w-full">
                <SelectValue placeholder="Selecione a posição" />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              <SelectItem value="top">
                Acima do campo
              </SelectItem>
              <SelectItem value="left">
                À Esquerda do Campo
              </SelectItem>
            </SelectContent>
          </Select>
          <FormMessage />
        </FormItem>
      </FormField>
    </div>

    <!-- Obrigatória -->
    <div class="border-b border-gray-02 py-4">
      <FormField
        v-slot="{ componentField }"
        name="required"
      >
        <FormItem class="flex items-center justify-between h-5">
          <FormLabel class="text-sm text-gray-07 font-normal cursor-pointer">
            Obrigatória
          </FormLabel>
          <FormControl>
            <Switch v-bind="componentField" />
          </FormControl>
        </FormItem>
      </FormField>
    </div>

    <!-- Tipo de número -->
    <div class="border-b border-gray-02 py-4">
      <FormField
        v-slot="{ componentField }"
        name="numberType"
      >
        <FormItem class="flex flex-wrap gap-6">
          <IconRadio
            v-for="option in NUMBER_TYPE_OPTIONS"
            :key="option.value"
            :data-testid="`number-type-radio-${option.value}`"
            :model-value="componentField.modelValue"
            :value="option.value"
            :label="option.label"
            :name="`number-type-${elementId ?? 'selected'}`"
            variant="medium"
            class="!font-normal"
            @update:model-value="componentField['onUpdate:modelValue']?.($event)"
          />
        </FormItem>
      </FormField>
    </div>

    <!-- Valor mínimo -->
    <div class="border-b border-gray-02 py-4">
      <FormField
        v-slot="{ componentField }"
        name="hasMinValue"
      >
        <FormItem class="flex items-center justify-between h-5">
          <FormLabel class="text-sm text-gray-07 font-normal cursor-pointer">
            Valor mínimo
          </FormLabel>
          <FormControl>
            <Switch v-bind="componentField" />
          </FormControl>
        </FormItem>
      </FormField>
      <div
        v-if="values.hasMinValue"
        class="mt-3"
      >
        <NumberTextField
          name="minValue"
          placeholder="Ex: 0"
          input-class="h-11 border-gray-02 text-gray-07"
          :typing="typing"
        />
      </div>
    </div>

    <!-- Valor máximo -->
    <div class="border-b border-gray-02 py-4">
      <FormField
        v-slot="{ componentField }"
        name="hasMaxValue"
      >
        <FormItem class="flex items-center justify-between h-5">
          <FormLabel class="text-sm text-gray-07 font-normal cursor-pointer">
            Valor máximo
          </FormLabel>
          <FormControl>
            <Switch v-bind="componentField" />
          </FormControl>
        </FormItem>
      </FormField>
      <div
        v-if="values.hasMaxValue"
        class="mt-3"
      >
        <NumberTextField
          name="maxValue"
          placeholder="Ex: 100"
          input-class="h-11 border-gray-02 text-gray-07"
          :typing="typing"
        />
      </div>
    </div>

    <!-- Quantidade de casas decimais -->
    <div
      v-if="values.numberType === 'decimal'"
      class="border-b border-gray-02 py-4"
    >
      <DecimalPlacesField />
    </div>
  </div>
</template>
